/**
 * cascadeHelpers.js (Iter230a — extracted from PenetrationEditPanel.js)
 * ────────────────────────────────────────────────────────────────────
 * Shared cascade primitives used by:
 *   - PenetrationEditPanel.js              (main dialog)
 *   - inline/PenetrationInlineCreate.js   (inline create — Iter229v)
 *   - inline/BacktrayInlineCreate.js      (inline create — Iter229w)
 *
 * Everything here is roof-profile / kind / variant lookup math —
 * no React, no API calls, pure data. Lives in its own module so the
 * three consumers stay in sync and a tweak to (e.g.) the Lead-base
 * variant rules doesn't accidentally diverge between the inline form
 * and the full edit dialog.
 */

// Pin Penetration kind inference. Pre-Iter229u Pipe/Flue legacy types
// were hard-deleted; Penetration pins always carry an explicit
// `penetration_kind`. Default missing values to 'pipe' (most common).
export const inferKind = (pin) => (pin.penetration_kind === 'flue' ? 'flue' : 'pipe');

// Default Ø options per kind. Diameters are the catalogue's most-
// frequent values; the operator can still override numerically.
export const KIND_DIAS = {
  pipe: [32, 40, 50, 80, 100],
  flue: [80, 100, 125, 140, 150, 160, 175, 240],
};

// Dektite-variant pills filtered to the roof's material family.
// `roofKey` is the output of `ridgeRoofKey(roofType)` below.
export const VARIANT_OPTS_FOR_ROOFKEY = (roofKey) => {
  const all = [
    { v: 'std',      label: 'Std',       tip: 'Std EPDM boot for metal roofs. Pipe is unobstructed.',                                  allow: ['iron'] },
    { v: 'retrofit', label: 'Retrofit',  tip: 'Cut+wrap EPDM Dektite for metal roofs. Use when pipe has fittings.',                    allow: ['iron'] },
    { v: 'lead',     label: 'Lead Base', tip: 'Malleable lead aquaseal for concrete / clay / tin-tile roofs.',                          allow: ['concrete', 'clay', 'tile'] },
    { v: 'ali',      label: 'Ali Base',  tip: 'Aluminium-base variant of Lead Base. Same roofs, non-lead finish.',                      allow: ['concrete', 'clay', 'tile'] },
  ];
  if (!roofKey || roofKey === 'unknown') return all;
  return all.filter(o => o.allow.includes(roofKey));
};

// Map an arbitrary roof-type string to one of {iron, concrete, clay,
// tile, unknown}. Used to pick the variant pills and default
// dektite-variant. Conservative regex — anything we don't recognise
// returns 'unknown' which lets the operator see ALL variants.
export const ridgeRoofKey = (roofType) => {
  const t = String(roofType || '').toLowerCase();
  if (/iron|longrun|long run|trimrib|corrugate/.test(t)) return 'iron';
  if (/concrete/.test(t)) return 'concrete';
  if (/clay/.test(t)) return 'clay';
  if (/tin.?tile|tile/.test(t)) return 'tile';
  return 'unknown';
};

// Normalise a roof-profile string for case- + punctuation-insensitive
// comparison. Used by BacktrayInlineCreate to match against the
// `sheet_profile` / `sheet_profiles` arrays on flashing_templates.
export const normProfile = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
